import { Alert as ReactAlert, Switch as ReactSwitch } from '@a-ui/react';
import { FormikProps, FormikValues, Field, FieldProps } from 'formik';
import React from 'react';
import { api } from '../../hooks/api.hooks';
import useClassNames from '../../hooks/classNames.hooks';
import { translate } from '../../i18n';
import { ITranslations } from '../../i18n/config';
import { useAuthentication } from '../../state/Authentication';
import Form from '../Form';
import { FormHelpers } from '../Form/Form';
import Typography from '../Typography';

interface SubscribeActionProps {
  listId: string;
}

const SubscribeAction: React.FC<SubscribeActionProps> = ({ listId }) => {
  const form = React.useRef<FormikProps<FormikValues>>(null);
  const [error, setError] = React.useState<ITranslations>();
  const [success, setSuccess] = React.useState<ITranslations>();
  const [isSwitchOn, setSwitchOn] = React.useState<boolean>(false); // Maintain switch state

  const {
    state: { user },
  } = useAuthentication();
  const { className } = useClassNames('ckp-a-subscribe');

  const handleSubmit = async (
    values: { [listId: string]: boolean },
    { setFieldValue }: FormHelpers,
  ) => {
    const doSubscribe = values[listId];
    setError(undefined);
    setSuccess(undefined);

    if (doSubscribe) {
      try {
        await api.post(`/newsletters/subscribe/${listId}`);
        setSuccess('newsletters.subscribe.success');
      } catch (e) {
        setError('error.newsletter.subscribed');
        setFieldValue(listId, false);
      }
    } else {
      try {
        await api.post(`/newsletters/unsubscribe/${listId}`);
        setSuccess('newsletters.unsubscribe.success');
      } catch (e) {
        setError('error.newsletter.unsubscribed');
        setFieldValue(listId, false);
      }
    }
  };

  const handleSwitchChange = () => {
    const newValue = !isSwitchOn; // Toggle the switch state
    setSwitchOn(newValue);

    // Update the form field
    form.current?.setFieldValue(listId, newValue);
    form.current?.submitForm();
  };

  const isSubscribed = React.useMemo(
    () => user.emailPrimary && user.newsletters?.includes(listId),
    [user, listId],
  );

  React.useEffect(() => {
    if (isSubscribed) {
      setSwitchOn(true); // Set isSwitchOn to true if user is subscribed
    }
  }, [isSubscribed]);

  return (
    <div className="ckp-a-subscribe__wrapper">
      {error && (
        <div className="u-margin-top-xs">
          <ReactAlert inline theme="danger">
            <Typography>{translate(error)}</Typography>
          </ReactAlert>
        </div>
      )}
      {success && (
        <div className="u-margin-top-xs">
          <ReactAlert inline theme="success">
            <Typography>{translate(success)}</Typography>
          </ReactAlert>
        </div>
      )}
      <div className={className}>
        <Form initialValues={{ [listId]: isSubscribed }} onSubmit={handleSubmit} ref={form}>
          <Field name={listId}>
            {({ field }: FieldProps<any>) => {
              return (
                <ReactSwitch
                  {...field}
                  checked={isSwitchOn}
                  statusLabelSide={'left'}
                  statusLabelOn={translate('newsletters.subscribe.on')}
                  statusLabelOff={translate('newsletters.subscribe.on')}
                  onClick={handleSwitchChange}
                />
              );
            }}
          </Field>
        </Form>
      </div>
    </div>
  );
};

export default SubscribeAction;
